import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Shield, Settings, BarChart3 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const CookieConsent = () => {
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    functional: false,
    analytics: false
  });

  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath.includes('/politica-privacidade') || currentPath.includes('/politica-cookies')) {
      setShowBanner(false);
      return;
    }
    const consent = localStorage.getItem('msgg_cookie_consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      setShowBanner(false);
    }
  }, [location.pathname]);

  const persist = (prefs) => {
    localStorage.setItem('msgg_cookie_consent', JSON.stringify(prefs));
    setShowBanner(false);
    setShowCustomize(false);
  };

  const handleAcceptAll = () => persist({ essential: true, functional: true, analytics: true });
  const handleAcceptEssential = () => persist({ essential: true, functional: false, analytics: false });
  const handleSavePreferences = () => persist(preferences);

  const togglePreference = (key) => {
    if (key === 'essential') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Compact Cookie Banner — Bottom corner, non-intrusive */}
      {!showCustomize && (
        <div
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[9999] animate-in slide-in-from-bottom-4 fade-in duration-300"
          role="dialog"
          aria-label="Aviso de cookies"
          data-testid="cookie-banner"
        >
          <div className="bg-white/95 backdrop-blur-md shadow-xl border border-gray-200 rounded-2xl p-4">
            <div className="flex items-start gap-3 mb-3">
              <Shield className="text-orange-500 flex-shrink-0 mt-0.5" size={18} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 leading-snug">
                  Usamos cookies para melhorar a sua experiência. Veja a nossa{' '}
                  <a href="/politica-cookies" className="text-blue-600 hover:underline font-medium">
                    Política de Cookies
                  </a>.
                </p>
              </div>
              <button
                onClick={handleAcceptEssential}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                aria-label="Recusar e fechar"
                data-testid="cookie-close-btn"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleAcceptAll}
                size="sm"
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium h-9"
                data-testid="cookie-accept-all-btn"
              >
                Aceitar
              </Button>
              <Button
                onClick={handleAcceptEssential}
                variant="outline"
                size="sm"
                className="flex-1 border-gray-300 hover:bg-gray-50 text-sm font-medium h-9"
                data-testid="cookie-essential-btn"
              >
                Essenciais
              </Button>
              <button
                onClick={() => setShowCustomize(true)}
                className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2 px-1"
                data-testid="cookie-customize-link"
              >
                Personalizar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customize Modal — opens only when user clicks "Personalizar" */}
      {showCustomize && (
        <>
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998] animate-in fade-in duration-200"
            onClick={() => setShowCustomize(false)}
          />
          <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none">
            <Card
              className="max-w-2xl w-full shadow-2xl border-none max-h-[85vh] flex flex-col overflow-hidden rounded-t-3xl sm:rounded-2xl pointer-events-auto animate-in slide-in-from-bottom-4 fade-in duration-300"
              data-testid="cookie-customize-modal"
            >
              <CardHeader className="relative pb-3 flex-shrink-0 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    Preferências de Cookies
                  </CardTitle>
                  <button
                    onClick={() => setShowCustomize(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Fechar"
                    data-testid="customize-close-btn"
                  >
                    <X size={20} />
                  </button>
                </div>
              </CardHeader>

              <CardContent className="space-y-3 overflow-y-auto flex-1 py-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                {/* Essential */}
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <Shield className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-1">Essenciais</h4>
                        <p className="text-xs text-gray-600">Necessários para o funcionamento do site. Não podem ser desativados.</p>
                      </div>
                    </div>
                    <div className="w-10 h-5 bg-blue-600 rounded-full flex items-center justify-end px-0.5 flex-shrink-0">
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </div>
                  </div>
                </div>

                {/* Functional */}
                <button
                  type="button"
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    preferences.functional
                      ? 'bg-purple-50 border-purple-300'
                      : 'bg-white border-gray-200 hover:border-purple-200'
                  }`}
                  onClick={() => togglePreference('functional')}
                  data-testid="cookie-functional-toggle"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <Settings className={`mt-0.5 flex-shrink-0 ${preferences.functional ? 'text-purple-600' : 'text-gray-400'}`} size={20} />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-1">Funcionais</h4>
                        <p className="text-xs text-gray-600">Melhoram a experiência do utilizador (ex: preferências de idioma).</p>
                      </div>
                    </div>
                    <div className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors flex-shrink-0 ${
                      preferences.functional ? 'bg-purple-600 justify-end' : 'bg-gray-300 justify-start'
                    }`}>
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </div>
                  </div>
                </button>

                {/* Analytics */}
                <button
                  type="button"
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    preferences.analytics
                      ? 'bg-orange-50 border-orange-300'
                      : 'bg-white border-gray-200 hover:border-orange-200'
                  }`}
                  onClick={() => togglePreference('analytics')}
                  data-testid="cookie-analytics-toggle"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <BarChart3 className={`mt-0.5 flex-shrink-0 ${preferences.analytics ? 'text-orange-600' : 'text-gray-400'}`} size={20} />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-1">Analytics</h4>
                        <p className="text-xs text-gray-600">Ajudam-nos a compreender como os visitantes usam o site (anónimo).</p>
                      </div>
                    </div>
                    <div className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors flex-shrink-0 ${
                      preferences.analytics ? 'bg-orange-600 justify-end' : 'bg-gray-300 justify-start'
                    }`}>
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </div>
                  </div>
                </button>

                <div className="text-xs text-gray-500 pt-2">
                  Ver{' '}
                  <a href="/politica-privacidade" className="text-blue-600 hover:underline">Política de Privacidade</a>
                  {' '}e{' '}
                  <a href="/politica-cookies" className="text-blue-600 hover:underline">Política de Cookies</a>.
                </div>
              </CardContent>

              <div className="border-t p-4 flex flex-col sm:flex-row gap-2 flex-shrink-0">
                <Button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium h-10"
                  data-testid="cookie-save-btn"
                >
                  Guardar Preferências
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="flex-1 border-gray-300 hover:bg-gray-50 text-sm font-medium h-10"
                  data-testid="cookie-accept-all-modal-btn"
                >
                  Aceitar Todos
                </Button>
              </div>
            </Card>
          </div>
        </>
      )}
    </>
  );
};

export default CookieConsent;
